import { apiService } from './apiService';

export const NIL_GUID = '00000000-0000-0000-0000-000000000000';

export type TenantDefaultsKeyType = 'Universal' | 'CoPilot';

export interface UpsertCopilotKeyDto {
  keyType: TenantDefaultsKeyType;
  copilotId: string;
  key: string;
  secret?: string;
  isActive?: boolean;
}

export interface CopilotKeyDto {
  id: string;
  keyType: string;
  copilotId: string;
  key: string;
  secret: string;
  tenantId: string;
  createdBy: string;
  isActive: boolean;
  createdOn: string;
  updatedOn: string;
}

export interface UpsertPlatformDefaultsDto {
  defaultVectorStore: string;
  defaultLlm: string;
  defaultEmbeddingProvider: string;
}

export interface PlatformDefaultsDto {
  id: string;
  defaultVectorStore: string;
  defaultLlm: string;
  defaultEmbeddingProvider: string;
  tenantId: string;
  createdBy: string;
  createdOn: string;
  updatedOn: string;
}

const isNotFound = (err: unknown): boolean => {
  if (!err || typeof err !== 'object') return false;
  const response = (err as { response?: { status?: number } }).response;
  return response?.status === 404;
};

export const getApiErrorMessage = (err: unknown, fallback: string): string => {
  if (!err || typeof err !== 'object') return fallback;
  const response = (err as { response?: { data?: { message?: string } } }).response;
  const message = response?.data?.message;
  return typeof message === 'string' && message.trim() ? message.trim() : fallback;
};

export const tenantDefaultsService = {
  upsertCopilotKey: (dto: UpsertCopilotKeyDto) =>
    apiService.put('/api/tenant-defaults/copilot-keys', dto, 'backend') as Promise<CopilotKeyDto>,

  getCopilotKeys: () =>
    apiService.get('/api/tenant-defaults/copilot', {}, 'backend') as Promise<CopilotKeyDto[]>,

  deleteCopilotKey: (id: string) =>
    apiService.delete(`/api/tenant-defaults/copilot-keys/${id}`, 'backend'),

  upsertPlatformDefaults: (dto: UpsertPlatformDefaultsDto) =>
    apiService.put('/api/tenant-defaults/platform', dto, 'backend') as Promise<PlatformDefaultsDto>,

  /** Returns null when platform defaults have not been set yet (404). */
  getPlatformDefaults: async (): Promise<PlatformDefaultsDto | null> => {
    try {
      return (await apiService.get(
        '/api/tenant-defaults/platform',
        {},
        'backend'
      )) as PlatformDefaultsDto;
    } catch (err) {
      if (isNotFound(err)) return null;
      throw err;
    }
  },
};
